import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Android, Email, LocationOn, Phone } from "@mui/icons-material";
import { PLAY_STORE_URL } from "../config/store";

const API_BASE_URL = process.env.REACT_APP_API;
const COMPANY = "Weps Technology";
const ADDRESS = "H.No. 383, Muzaffarabad, Saharanpur, Uttar Pradesh, India – 247129";

const ContactUs = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(true);
        setFormData({ name: "", email: "", mobile: "", message: "" });
      } else {
        setError(data.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Unable to send your message. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "white",
    },
  };

  return (
    <Box sx={{ bgcolor: "#f7f5fb", pb: 8 }}>
      <Box
        sx={{
          color: "white",
          background: `linear-gradient(160deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, #7E57C2 100%)`,
          py: { xs: 6, md: 8 },
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              opacity: 0.95,
              mb: 1.5,
            }}
          >
            Support
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.9rem", md: "2.5rem" },
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              mb: 1.5,
            }}
          >
            Contact Treasure Support
          </Typography>
          <Typography sx={{ opacity: 0.94, lineHeight: 1.7, maxWidth: 560, fontSize: "1.05rem" }}>
            Have a question or feedback about Treasure? Send a message and we will get back to you.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: { xs: -3, md: -4 }, px: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          <Stack spacing={2}>
            <Box
              sx={{
                p: 3,
                bgcolor: "white",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "rgba(94,53,177,0.12)",
                boxShadow: "0 8px 24px rgba(94,53,177,0.06)",
              }}
            >
              <Typography fontWeight={800} sx={{ mb: 2 }}>
                {COMPANY}
              </Typography>
              <Stack spacing={1.75}>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                  <LocationOn sx={{ color: "primary.main", mt: 0.25 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    {ADDRESS}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  <Email sx={{ color: "primary.main" }} />
                  <Typography variant="body2" color="text.secondary">
                    Use the form to reach support
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  <Phone sx={{ color: "primary.main" }} />
                  <Typography variant="body2" color="text.secondary">
                    Include your mobile number so we can follow up if needed
                  </Typography>
                </Box>
              </Stack>
              <Button
                component="a"
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<Android />}
                fullWidth
                sx={{ mt: 3, textTransform: "none", fontWeight: 700, borderRadius: 999 }}
              >
                Get Treasure on Google Play
              </Button>
              <Button
                component={RouterLink}
                to="/how-to-use"
                fullWidth
                sx={{ mt: 1, textTransform: "none", fontWeight: 600 }}
              >
                How to use Treasure
              </Button>
            </Box>
          </Stack>

          <Box
            sx={{
              p: { xs: 2.5, md: 3 },
              bgcolor: "white",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "rgba(94,53,177,0.12)",
              boxShadow: "0 8px 24px rgba(94,53,177,0.06)",
            }}
          >
            <Typography fontWeight={800} sx={{ mb: 0.75 }}>
              Send a message
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.65 }}>
              Tell us what you need help with—app questions, feedback, or account support.
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                Thank you for contacting us. We have received your message and will get back to you soon.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  label="Mobile number"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  label="Message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  multiline
                  rows={4}
                  fullWidth
                  sx={fieldSx}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  sx={{
                    mt: 0.5,
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 999,
                    py: 1.25,
                  }}
                >
                  {submitting ? <CircularProgress size={22} color="inherit" /> : "Send message"}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ContactUs;
